import {
    Button,
    Divider,
    Footer,
    FooterBar,
    FooterBarLink,
    Header,
    HeaderBrand,
    HeaderLogotypeSecondary,
    Hero,
    Logotype,
    Modal,
    SystemIcon,
    Typography,
} from "@volvo/vcdk-react";
import styles from "./LandingPage.module.css";
import { useRef, useState, useCallback } from "react";
import { HeaderLogotypePrimary } from "@volvo/vcdk-react/HeaderLogotypePrimary";
import RegisterForm from "../RegisterForm/RegisterForm";
import FloatingSupportButton from "../FloatingSupportButton/FloatingSupportButton";
import heroXs from "../../../assets/images/hero/xs.webp";
import heroSm from "../../../assets/images/hero/sm.webp";
import heroMd from "../../../assets/images/hero/md.webp";
import heroLg from "../../../assets/images/hero/lg.webp";
import { VolvoFooter } from "../../../constants/const";

const LandingPage = () => {
    const year = new Date().getFullYear();
    const textContainerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [modalKey, setModalKey] = useState(Date.now());

    const handleLearnMoreClick = useCallback(() => {
        if (textContainerRef.current) {
            textContainerRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    const openRegister = useCallback(() => {
        setModalKey(Date.now());
        setIsOpen(true);
    }, []);

    return (
        <div className={styles.LandingPageContainer}>
            <div>
                <Header className={styles.HeaderContainer}>
                    <HeaderBrand>
                        <HeaderLogotypePrimary logotype="volvo-spread-word-mark" />
                        <HeaderLogotypeSecondary>
                            <Logotype variant="renault-trucks-horizontal" />
                        </HeaderLogotypeSecondary>
                    </HeaderBrand>
                </Header>
            </div>
            <div>
                <div className={styles.HeroContainer}>
                    <Hero
                        srcset={`${heroXs} 601w, ${heroSm} 1001w, ${heroMd} 2001w, ${heroLg} 4001w`}
                    >
                        <Typography variant="display1">
                            Volvo Group <br />
                            Data Act Request Tool
                        </Typography>
                        <span slot="overline">Welcome to the</span>
                        <p slot="body">For Third-Party Data Sharing Requests</p>
                        <Button
                            slot="cta"
                            variant="marketing"
                            invertTheme
                            onClick={openRegister}
                        >
                            Request
                        </Button>
                        <Button
                            slot="cta"
                            variant="secondary"
                            onClick={handleLearnMoreClick}
                        >
                            Learn more
                        </Button>
                    </Hero>
                </div>
                <div className={styles.textContainer} ref={textContainerRef}>
                    {" "}
                    <br />
                    <Typography variant="body" className={styles.justifyText}>
                        This tool is designed to receive data sharing requests
                        initiated by third party data recipients who are acting
                        on behalf of users of any connected product(s) <br />
                        and/or digital service(s) (including related services)
                        for which Volvo Trucks, Volvo Buses, Volvo Energy, or Renault Trucks
                        <br />
                        is the data holder, in accordance with the Data
                        Act/Regulation (EU) 2023/2854 (the 'Data Act'). <br />
                        If you are the user of any connected product(s) and/or
                        digital service(s) for which Volvo Trucks, Volvo Buses,
                        Volvo Energy, or Renault Trucks <br />
                        is the data holder pursuant to the Data Act and you want
                        to retrieve product, and where applicable, related
                        services data under the Data Act, check out the
                        respective brands’ Data Act webpages.
                    </Typography>
                </div>
                <div className={styles.bodySection}>
                    <Divider />
                    <div className={styles.textContainer}>
                        <Typography variant="body">
                            In order to complete this data sharing request, you
                            will need to provide
                        </Typography>
                    </div>
                    <div className={styles.infoSection}>
                        <div>
                            <Typography variant="body">
                                <ol>
                                    <Typography
                                        variant="subtitle1"
                                        className={styles.infoSectionHeader}
                                    >
                                        Information about yourself
                                    </Typography>
                                    <br />
                                    <br />
                                    <Typography
                                        variant="body"
                                        style={{
                                            textAlign: "left",
                                            color: "#53565a",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                            }}
                                        >
                                            <SystemIcon icon="turn-signals-directional-indicators-right-side" />
                                            <p>
                                                A copy of an identity document.
                                                You can use any identity
                                                document that is accepted as
                                                proof of identity in an EU
                                                country.
                                            </p>
                                        </div>
                                    </Typography>
                                </ol>
                            </Typography>
                        </div>
                        <Divider vertical />
                        <div>
                            <Typography variant="body">
                                <ol>
                                    <Typography
                                        variant="subtitle1"
                                        className={styles.infoSectionHeader}
                                    >
                                        Information about Third party data
                                        recipient
                                    </Typography>
                                    <br />
                                    <br />
                                    <Typography variant="body">
                                        <div
                                            style={{
                                                display: "flex",
                                                color: "#53565a",
                                            }}
                                        >
                                            <SystemIcon icon="turn-signals-directional-indicators-right-side" />
                                            <p>
                                                The certificate of registration
                                                of the third party data
                                                recipient.
                                            </p>
                                        </div>
                                    </Typography>
                                </ol>
                            </Typography>
                        </div>
                        <Divider vertical />
                        <div>
                            <Typography variant="body">
                                <ol>
                                    <Typography
                                        variant="subtitle1"
                                        className={styles.infoSectionHeader}
                                    >
                                        Information about the user
                                    </Typography>
                                    <br />
                                    <br />
                                    <Typography
                                        variant="body"
                                        style={{ textAlign: "left" }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                color: "#53565a",
                                            }}
                                        >
                                            <SystemIcon icon="turn-signals-directional-indicators-right-side" />
                                            <p>
                                                The name of the user who has
                                                mandated the third party data
                                                recipient as it appears in
                                                customer portal (Volvo Connect
                                                for Volvo Buses, Volvo Energy
                                                and Volvo Trucks; Renault Trucks
                                                Customer Portal for Renault
                                                Trucks)
                                            </p>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                color: "#53565a",
                                            }}
                                        >
                                            <SystemIcon icon="turn-signals-directional-indicators-right-side" />
                                            <p>
                                                The name of its fleet in the
                                                customer portal
                                            </p>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                color: "#53565a",
                                            }}
                                        >
                                            <SystemIcon icon="turn-signals-directional-indicators-right-side" />
                                            <p>
                                                The email address of its fleet
                                                admin in the customer portal,
                                                and
                                            </p>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                color: "#53565a",
                                            }}
                                        >
                                            <SystemIcon icon="turn-signals-directional-indicators-right-side" />
                                            <p>
                                                The VIN or chassis number of one
                                                of the user's connected products
                                                eligible to the Data Act.
                                            </p>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                color: "#53565a",
                                                textAlign: "center",
                                            }}
                                        >
                                            <p
                                                className={
                                                    styles.infoSectionNote
                                                }
                                            >
                                                <br />
                                                This information shall be
                                                provided to you by the user when
                                                requesting you to act on its
                                                behalf.
                                            </p>
                                        </div>
                                    </Typography>
                                </ol>
                            </Typography>
                        </div>
                    </div>
                    <div className={styles.textContainer}>
                        <Typography variant="body">
                            <p>
                                Make sure you have all requested information as
                                you will not be able to save a draft of your
                                request and come back later.
                            </p>
                        </Typography>
                    </div>
                </div>
            </div>
            <div className={styles.FooterContainer}>
                <Footer>
                    <FooterBar
                        copyright={`© Copyright AB Volvo ${year}`}
                        logotype={`volvo-spread-word-mark`}
                    >
                        <FooterBarLink href={VolvoFooter.VolvoPrivacy}>
                            Privacy
                        </FooterBarLink>
                    </FooterBar>
                </Footer>
            </div>
            <Modal
                className={styles.Modal}
                open={isOpen}
                onClose={() => setIsOpen(false)}
            >
                <RegisterForm key={modalKey} />
            </Modal>
            <FloatingSupportButton />
        </div>
    );
};

export default LandingPage;
